// src/components/Sales/SupervisorAllowanceList.tsx

import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Collapse,
  IconButton,
  useTheme,
  useMediaQuery,
  Divider
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { 
  SupervisorAllowance, 
  SupervisorAllowanceSummary,
  SalesResult,
  formatCurrency,
  formatDate,
  formatManagerStatus
} from '../../types/sales';

interface SupervisorAllowanceListProps {
  month: string;
  allowances: SupervisorAllowance[];
  summaries: SupervisorAllowanceSummary[];
  loading: boolean;
  error: string | null;
  salesData?: SalesResult[]; // 売上データを受け取れるようにする
}

export const SupervisorAllowanceList: React.FC<SupervisorAllowanceListProps> = ({
  month,
  allowances,
  summaries,
  loading,
  error,
  salesData = []
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  // 非担当の職長手当データのみをフィルタリング
  const filteredAllowances = useMemo(() => {
    return allowances.filter(allowance => !allowance.is_manager);
  }, [allowances]);

  // 職長手当データを売上計上月と請求月でグループ化
//   const groupedAllowances = useMemo(() => {
//     // 売上IDと請求月のマッピングを作成
//     const salesResultMap: Record<number, string> = {};
//     salesData.forEach(sale => {
//       salesResultMap[sale.id] = sale.billing_month.substring(0, 7);
//     });

//     // 現在の計上月のデータ
//     const currentMonthData: SupervisorAllowance[] = [];
//     // 請求月別のデータ
//     const billingMonthGroups: Record<string, SupervisorAllowance[]> = {};

//     filteredAllowances.forEach(allowance => {
//       // 紐づく売上データの請求月を取得
//       const billingYearMonth = salesResultMap[allowance.sales_result_id];
      
//       // 売上計上月と請求月が同じか、請求月情報がない場合は現在の計上月グループに追加
//       if (!billingYearMonth || billingYearMonth === month) {
//         currentMonthData.push(allowance);
//       } else {
//         // 請求月が異なる場合は請求月グループに追加
//         if (!billingMonthGroups[billingYearMonth]) {
//           billingMonthGroups[billingYearMonth] = [];
//         }
//         billingMonthGroups[billingYearMonth].push(allowance);
//       }
//     });

//     // 請求月グループを日付降順（新しい順）にソート
//     const sortedBillingMonths = Object.keys(billingMonthGroups).sort().reverse();
    
//     return {
//       currentMonthData,
//       billingMonthGroups,
//       sortedBillingMonths
//     };
//   }, [filteredAllowances, salesData, month]);

// 職長手当データを売上計上月と請求月でグループ化
const groupedAllowances = useMemo(() => {
    // 現在の計上月のデータ
    const currentMonthData: SupervisorAllowance[] = [];
    // 請求月別のデータ
    const billingMonthGroups: Record<string, SupervisorAllowance[]> = {};
  
    filteredAllowances.forEach(allowance => {
      // allowanceから直接billing_monthとsales_monthを取得（年月部分のみ）
      const salesYearMonth = allowance.sales_month?.substring(0, 7) || '';
      const billingYearMonth = allowance.billing_month?.substring(0, 7) || '';
      
      // 現在の月（filter.month）と比較
      if (!billingYearMonth || billingYearMonth === month) {
        currentMonthData.push(allowance);
      } else {
        // 請求月が異なる場合は請求月グループに追加
        if (!billingMonthGroups[billingYearMonth]) {
          billingMonthGroups[billingYearMonth] = [];
        }
        billingMonthGroups[billingYearMonth].push(allowance);
      }
    });
  
    // 請求月グループを日付降順（新しい順）にソート
    const sortedBillingMonths = Object.keys(billingMonthGroups).sort().reverse();
    
    return {
      currentMonthData,
      billingMonthGroups,
      sortedBillingMonths
    };
  }, [filteredAllowances, month]);

  // 職長手当集計データもグループ化
  const groupedSummaries = useMemo(() => {
    // 現状では集計データは全て表示する（グループ化しない）
    return {
      currentMonthData: summaries,
      billingMonthGroups: {},
      sortedBillingMonths: []
    };
  }, [summaries]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleExpandRow = (id: number) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  const yearMonthDisplay = month ? `${month.substring(0, 4)}年${parseInt(month.substring(5, 7))}月` : '';

  // 日付を表示用に変換（YYYY-MM → YYYY年MM月）
  const formatYearMonth = (yearMonth: string): string => {
    const [year, month] = yearMonth.split('-');
    return `${year}年${parseInt(month)}月`;
  };

  // 職長別集計カードをレンダリング
  const renderSummaryCards = (data: SupervisorAllowanceSummary[]) => (
    <Box p={2}>
      {data.length === 0 ? (
        <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>
          該当データがありません
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {data.map((summary, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {summary.supervisor_name}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    工事件数: {summary.project_count}件
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    総日数: {summary.total_days}日
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    担当日数: {summary.manager_days}日 / 非担当日数: {summary.non_manager_days}日
                  </Typography>
                  <Typography variant="h5" sx={{ mt: 2, color: theme.palette.primary.main }}>
                    {formatCurrency(summary.total_allowance)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );

  // 詳細一覧テーブルをレンダリング
  const renderDetailTable = (data: SupervisorAllowance[]) => (
    <TableContainer>
      {data.length === 0 ? (
        <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>
          該当データがありません
        </Typography>
      ) : (
        <Table size={isSmallScreen ? "small" : "medium"}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>職長名</TableCell>
              <TableCell>会社名</TableCell>
              <TableCell>現場名</TableCell>
              <TableCell align="right">手当額</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((allowance) => (
              <React.Fragment key={allowance.id}>
                <TableRow
                  hover
                  onClick={() => handleExpandRow(allowance.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox">
                    <IconButton size="small">
                      {expandedRows[allowance.id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>{allowance.supervisor_name}</TableCell>
                  <TableCell>{allowance.Customer_Name || '-'}</TableCell>
                  <TableCell>{allowance.Site_Name || '-'}</TableCell>
                  <TableCell align="right">{formatCurrency(allowance.allowance_amount)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 0 }}>
                    <Collapse in={expandedRows[allowance.id] || false} timeout="auto" unmountOnExit>
                      <Box sx={{ py: 2, px: 3 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2">工事情報</Typography>
                            <Typography variant="body2">日付: {allowance.supervisor_date_str || '-'}</Typography>
                            <Typography variant="body2">担当者: {allowance.Manager_Name || '-'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2">手当情報</Typography>
                            <Typography variant="body2">金額: {formatCurrency(allowance.allowance_amount)}</Typography>
                            <Typography variant="body2">作成日: {formatDate(allowance.created_at)}</Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      )}
    </TableContainer>
  );

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" component="h2" gutterBottom>
        職長手当一覧 ({yearMonthDisplay})
      </Typography>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="職長別集計" />
          <Tab label="詳細一覧" />
        </Tabs>

        {/* 職長別集計 */}
        {tabValue === 0 && renderSummaryCards(groupedSummaries.currentMonthData)}

        {/* 詳細一覧 */}
        {tabValue === 1 && (
          <Box>
            {/* 現在の計上月のデータ */}
            {renderDetailTable(groupedAllowances.currentMonthData)}
            
            {/* 請求月が異なるデータのグループ */}
            {groupedAllowances.sortedBillingMonths.length > 0 && (
              <>
                {/* 過去分のグループを表示 */}
                {groupedAllowances.sortedBillingMonths.map(billingMonth => (
                  <React.Fragment key={billingMonth}>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" sx={{ px: 2, mb: 2, fontWeight: 500 }}>
                      {formatYearMonth(billingMonth)}分として
                    </Typography>
                    {renderDetailTable(groupedAllowances.billingMonthGroups[billingMonth])}
                  </React.Fragment>
                ))}
              </>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};